/**
 * Pict-Provider-SessionAPI
 * ------------------------
 * Browser-side client for the server's session storage. Backs the
 * calendar/timeline browse, the upload flow from the dropzone, and the
 * "click into a session" download path.
 *
 * AppData shape this provider owns:
 *   AppData.Sessions = {
 *       List:       [],            // server rows (sorted newest first)
 *       Index:      {},            // IDSessionFile → row
 *       ByDate:     {},            // 'YYYY-MM-DD' → [row, row, …]
 *       SelectedID: 0,             // currently-open session
 *       LastLoadedAt: 0,
 *       LoadError:  null,
 *       Uploading:  false,
 *       UploadProgress: 0          // 0..1
 *   }
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'SessionAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

class PictProviderSessionAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);

		this.routePrefix = this.options.RoutePrefix;
	}

	initializeAppData()
	{
		if (!this.fable.AppData.Sessions)
		{
			this.fable.AppData.Sessions =
			{
				List: [],
				Index: {},
				ByDate: {},
				SelectedID: 0,
				LastLoadedAt: 0,
				LoadError: null,
				Uploading: false,
				UploadProgress: 0
			};
		}
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') tmpPrefix += '/';
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	_indexList(pList)
	{
		this.initializeAppData();
		let tmpStore = this.fable.AppData.Sessions;
		tmpStore.List = pList.slice().sort((pA, pB) =>
		{
			let tmpA = pA.SessionStartedAt || pA.CreateDate || '';
			let tmpB = pB.SessionStartedAt || pB.CreateDate || '';
			return tmpB.localeCompare(tmpA);
		});
		tmpStore.Index = {};
		tmpStore.ByDate = {};
		for (let i = 0; i < tmpStore.List.length; i++)
		{
			let tmpRow = tmpStore.List[i];
			tmpStore.Index[tmpRow.IDSessionFile] = tmpRow;
			let tmpKey = this._dateKey(tmpRow.SessionStartedAt || tmpRow.CreateDate);
			if (tmpKey)
			{
				if (!tmpStore.ByDate[tmpKey]) tmpStore.ByDate[tmpKey] = [];
				tmpStore.ByDate[tmpKey].push(tmpRow);
			}
		}
		tmpStore.LastLoadedAt = Date.now();
	}

	_dateKey(pTimestamp)
	{
		if (!pTimestamp) return '';
		let tmpDate = new Date(pTimestamp);
		if (isNaN(tmpDate.getTime())) return '';
		let tmpY = tmpDate.getFullYear();
		let tmpM = String(tmpDate.getMonth() + 1).padStart(2, '0');
		let tmpD = String(tmpDate.getDate()).padStart(2, '0');
		return `${tmpY}-${tmpM}-${tmpD}`;
	}

	listSessions()
	{
		this.initializeAppData();
		return libFrameworkClient.getJSON(this.fable, this._url('SessionFiles'))
			.then((pBody) =>
			{
				let tmpRows = Array.isArray(pBody) ? pBody : (pBody && pBody.Records) || [];
				this._indexList(tmpRows);
				this.fable.AppData.Sessions.LoadError = null;
				return tmpRows;
			})
			.catch((pErr) =>
			{
				this.fable.AppData.Sessions.LoadError = pErr.message;
				return [];
			});
	}

	/**
	 * Two-stage upload:
	 *   1. POST metadata to /1.0/Session/New  → returns SessionFile row with ParimeHash
	 *   2. PUT raw zip to   /1.0/Session/:id/Blob
	 *
	 * pMetadata should be derived from the existing SessionParser output:
	 *   { Title, OriginalFileName, SessionStartedAt, SessionLastActivityAt,
	 *     TotalDurationMs, PromptCount, ToolCallCount, AgentCount, Model }
	 * pBlob is the original ArrayBuffer / Blob the user dropped.
	 */
	uploadSession(pMetadata, pBlob)
	{
		this.initializeAppData();
		let tmpStore = this.fable.AppData.Sessions;
		tmpStore.Uploading = true;
		tmpStore.UploadProgress = 0.05;

		return libFrameworkClient.postJSON(this.fable, this._url('Session/New'), pMetadata)
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success || !pBody.SessionFile)
				{
					throw new Error((pBody && pBody.Error) || 'Could not register session.');
				}
				tmpStore.UploadProgress = 0.3;
				let tmpRow = pBody.SessionFile;

				let tmpBlob = pBlob;
				if (pBlob instanceof ArrayBuffer)
				{
					tmpBlob = new Blob([pBlob], { type: 'application/zip' });
				}

				return libFrameworkClient.uploadBinary(this.fable, this._url('Session/' + tmpRow.IDSessionFile + '/Blob'), tmpBlob, 'application/octet-stream', 'PUT')
					.then((pPutBody) =>
					{
						tmpStore.UploadProgress = 1.0;
						tmpStore.Uploading = false;
						// Patch the size in our local row + re-index.
						if (pPutBody && pPutBody.FileSizeBytes)
						{
							tmpRow.FileSizeBytes = pPutBody.FileSizeBytes;
						}
						let tmpList = tmpStore.List.slice();
						tmpList.unshift(tmpRow);
						this._indexList(tmpList);
						return tmpRow;
					});
			})
			.catch((pErr) =>
			{
				tmpStore.Uploading = false;
				tmpStore.UploadProgress = 0;
				tmpStore.LoadError = pErr.message;
				throw pErr;
			});
	}

	/**
	 * Stream the raw zip back from the server. Returns an ArrayBuffer that the
	 * existing SessionParser.parseZip can consume directly.
	 */
	downloadSessionBlob(pIDSessionFile)
	{
		return libFrameworkClient.getBinary(this.fable, this._url('Session/' + pIDSessionFile + '/Blob'));
	}

	/**
	 * The Story timeline for a Work Item: the chronological set of sessions that
	 * advanced it (union of the scalar link and the SessionFileWorkItem join),
	 * each carrying its parsed metadata snapshot and per-link outcome. Resolves
	 * to the Sessions array (oldest first); resolves to [] on any error so the
	 * panel degrades to an empty story rather than throwing.
	 */
	getWorkItemStory(pIDWorkItem)
	{
		return libFrameworkClient.getJSON(this.fable, this._url('WorkItem/' + pIDWorkItem + '/Story'))
			.then((pBody) =>
			{
				// The full loop timeline (feedback / prompt / run / session / validated), oldest first.
				if (pBody && Array.isArray(pBody.Entries)) { return pBody.Entries; }
				// Back-compat with an older server that only returns Sessions.
				if (pBody && Array.isArray(pBody.Sessions)) { return pBody.Sessions.map((pS) => Object.assign({ Kind: 'session', At: pS.SessionStartedAt || pS.CreateDate || '', ID: pS.IDSessionFile, Outcome: pS.WorkOutcome }, pS)); }
				return [];
			})
			.catch(() => []);
	}

	/**
	 * The Claude sessions that built a Project: the runs whose whole session was filed
	 * against this Project (scalar SessionFile.IDProject), newest first, each carrying its
	 * parsed metadata snapshot. Resolves to the Sessions array, or [] on any error so the
	 * Project panel degrades to an empty list rather than throwing.
	 */
	projectSessions(pIDProject)
	{
		return libFrameworkClient.getJSON(this.fable, this._url('Project/' + pIDProject + '/Sessions'))
			.then((pBody) => (pBody && Array.isArray(pBody.Sessions)) ? pBody.Sessions : [])
			.catch(() => []);
	}

	/**
	 * Linked-session counts for every Work Item in the tenant, keyed
	 * IDWorkItem -> count (union of the scalar link and the SessionFileWorkItem
	 * join, deduped per item). Feeds the Work Items list session indicator column
	 * in one call rather than one /Story per row. Resolves to {} on any error so
	 * the column degrades to "no marker" rather than throwing.
	 */
	sessionCounts()
	{
		return libFrameworkClient.getJSON(this.fable, this._url('Session/WorkItemCounts'))
			.then((pBody) => (pBody && pBody.Counts && typeof pBody.Counts === 'object') ? pBody.Counts : {})
			.catch(() => ({}));
	}

	/**
	 * Link/patch a session row (attach it to a Feature/WorkItem, set an
	 * outcome). PUT to the generic meadow-endpoints update; tenancy-scoped.
	 */
	setLink(pIDSessionFile, pPatch)
	{
		let tmpBody = Object.assign({ IDSessionFile: pIDSessionFile }, pPatch || {});
		return libFrameworkClient.putJSON(this.fable, this._url('SessionFile'), tmpBody);
	}

	deleteSession(pIDSessionFile)
	{
		return libFrameworkClient.delJSON(this.fable, this._url('SessionFile/' + pIDSessionFile))
			.then(() =>
			{
				let tmpRow = this.fable.AppData.Sessions.Index[pIDSessionFile];
				if (tmpRow)
				{
					let tmpList = this.fable.AppData.Sessions.List.filter((r) => r.IDSessionFile !== pIDSessionFile);
					this._indexList(tmpList);
				}
				return true;
			})
			.catch((pErr) =>
			{
				this.fable.AppData.Sessions.LoadError = pErr.message;
				return false;
			});
	}

	// ── generic EntityList contract ──────────────────────────────────────────
	// Session renders through the standard Plansheet-EntityList, which drives a
	// provider via list()/store()/update()/remove(). Map those onto the session
	// methods so the list, rename, and delete work with no special-casing in the
	// list view. (Create is intentionally absent: sessions arrive by upload, not
	// an inline form, so the descriptor is flagged NoCreate.)
	list() { return this.listSessions(); }
	store() { this.initializeAppData(); return this.fable.AppData.Sessions; }
	update(pID, pRecord) { return this.setLink(pID, pRecord); }
	remove(pID) { return this.deleteSession(pID); }
}

module.exports = PictProviderSessionAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
